<template>
  <div class="main-screen">
    <div class="content-wrap">
      <v-card class="discover-card mb-3">
        <v-card-title
          class="discover-title"
          @click="discoverExpanded = !discoverExpanded"
        >
          <v-icon left>search</v-icon>
          Discover wrestlers
          <v-spacer />
          <v-icon>{{
            discoverExpanded ? "expand_less" : "expand_more"
          }}</v-icon>
        </v-card-title>
        <v-expand-transition>
          <div v-show="discoverExpanded" class="discover-body">
            <v-layout row wrap class="discover-filters">
              <v-flex xs12 sm6 md3>
                <v-text-field
                  v-model="discoverSearch"
                  label="Name or school"
                  placeholder="Search..."
                  clearable
                  hide-details
                  outlined
                  dense
                />
              </v-flex>
              <v-flex xs12 sm6 md2>
                <v-select
                  v-model="discoverLeague"
                  :items="leagueNames"
                  label="League"
                  clearable
                  hide-details
                  outlined
                  dense
                />
              </v-flex>
              <v-flex xs12 sm6 md2>
                <v-select
                  v-model="discoverWeight"
                  :items="allWeights"
                  label="Weight"
                  clearable
                  hide-details
                  outlined
                  dense
                />
              </v-flex>
              <v-flex xs12 sm6 md2>
                <v-text-field
                  v-model.number="discoverMinSkill"
                  label="Min skill"
                  type="number"
                  min="0"
                  hide-details
                  outlined
                  dense
                />
              </v-flex>
              <v-flex xs12 sm6 md2>
                <v-text-field
                  v-model.number="discoverMaxSkill"
                  label="Max skill"
                  type="number"
                  min="0"
                  hide-details
                  outlined
                  dense
                />
              </v-flex>
              <v-flex xs12 sm6 md2 class="discover-search-btn-wrap">
                <v-btn
                  color="primary"
                  outline
                  block
                  class="discover-search-btn"
                  :loading="discoverLoading"
                  @click="runDiscoverQuery"
                >
                  Search wrestlers
                </v-btn>
              </v-flex>
            </v-layout>
            <p
              v-if="!discoverLoading && !discoverHasSearched"
              class="discover-hint"
            >
              Choose a <strong>league</strong> (and optional weight), then click
              <strong>Search wrestlers</strong>. Or enter at least
              <strong>2 characters</strong> to search by
              <strong>first name</strong>, <strong>last name</strong>,
              <strong>full name</strong>, or <strong>school</strong> prefix
              (all leagues, or narrow with the league filter after).
            </p>
            <div v-if="discoverLoading" class="discover-loading">
              <v-progress-circular indeterminate color="primary" size="32" />
              <span>Loading wrestlers…</span>
            </div>
            <div v-else-if="discoverHasSearched" class="discover-results">
              <template v-if="(filteredDiscoverWrestlers || []).length === 0">
                <p class="discover-empty">
                  No wrestlers match your filters. Try adjusting search or
                  filters.
                </p>
              </template>
              <template v-else>
                <div
                  v-for="w in filteredDiscoverWrestlers || []"
                  :key="w.id"
                  class="discover-row"
                  @click="$router.push('/wrestler/' + w.id)"
                >
                  <div class="discover-avatar-wrap">
                    <img
                      v-if="authenticated && w.data.Image"
                      :src="w.data.Image"
                      class="discover-avatar"
                      alt=""
                    />
                    <img
                      v-else
                      :src="require('../assets/default-profile.png')"
                      class="discover-avatar"
                      alt=""
                    />
                  </div>
                  <div class="discover-info">
                    <span class="discover-name"
                      >{{ w.data.FirstName }} {{ w.data.LastName }}</span
                    >
                    <span class="discover-meta">
                      {{ w.data.School || "—" }} ·
                      {{ w.data.RankLeague || "—" }}
                      {{ w.data.RankWeightClass || "" }}
                    </span>
                    <span class="discover-record"
                      >({{ w.data.Wins || 0 }}–{{ w.data.Losses || 0 }}) · Skill
                      {{
                        formatSkillLevel(
                          w.data.Skill != null ? w.data.Skill : 0
                        )
                      }}</span
                    >
                  </div>
                  <v-icon class="discover-arrow" small>chevron_right</v-icon>
                </div>
              </template>
            </div>
          </div>
        </v-expand-transition>
      </v-card>

      <section class="leagues-section">
        <h2 class="section-title">Leagues</h2>
        <div v-if="leaguesLoading" class="leagues-loading">
          <v-progress-circular indeterminate color="primary" size="32" />
          <span>Loading leagues…</span>
        </div>
        <div v-else-if="leagues.length === 0" class="leagues-empty">
          <p class="leagues-empty-msg">No leagues yet.</p>
          <p class="leagues-empty-hint">
            Create and manage leagues from the menu.
          </p>
          <v-btn
            v-if="canManageLeagues"
            :to="{ name: 'league-manager' }"
            color="primary"
            small
            class="leagues-empty-btn"
          >
            Manage leagues
          </v-btn>
        </div>
        <div
          v-else-if="groupedLeaguesByState.length === 0"
          class="leagues-empty"
        >
          <p class="leagues-empty-msg">No leagues on the home screen.</p>
          <p class="leagues-empty-hint">
            Divisions can be hidden in League Management while staying available
            there for admins.
          </p>
          <v-btn
            v-if="canManageLeagues"
            :to="{ name: 'league-manager' }"
            color="primary"
            small
            class="leagues-empty-btn"
          >
            Manage leagues
          </v-btn>
        </div>
        <template v-else>
          <div
            v-for="state in groupedLeaguesByState"
            :key="state.name"
            class="leagues-state-group"
          >
            <h3 class="leagues-state-title">{{ state.name }}</h3>
            <div class="leagues-state-inner">
              <div
                v-for="league in state.leagues"
                :key="state.name + '-' + league.name"
                class="leagues-league-group"
              >
                <h4 class="leagues-league-title">{{ league.name }}</h4>
                <div class="leagues-row">
                  <div
                    v-for="division in league.divisions"
                    :key="division.id"
                    class="league-card"
                  >
                    <router-link
                      :to="{
                        name: 'league-rankings',
                        params: { leagueId: division.id },
                      }"
                      class="league-card-inner"
                    >
                      <div class="league-card-logo">
                        <img
                          :src="division.data.Logo || defaultLeagueLogo"
                          :alt="division.data.Name"
                          @error="onLeagueLogoError"
                        />
                      </div>
                      <span class="league-card-name">{{
                        division.data.Name
                      }}</span>
                      <span class="league-card-btn">See rankings</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </section>

      <section class="match-feed-section">
        <kennected-match-feed />
      </section>

      <section class="database-totals-section">
        <database-totals-cards />
      </section>

      <p class="home-attribution">
        IKWF club, wrestler, and match data provided by
        <a
          href="https://www.ikwf-history.org"
          target="_blank"
          rel="noopener noreferrer"
          class="home-attribution-link"
          >IKWF-History.org</a
        >.
      </p>
    </div>

    <kennected-add-wrestler
      :dialog="addWrestlerDialog"
      @close-addWrestler="addWrestlerDialog = false"
    />
    <kennected-add-match
      :dialog="addMatchDialog"
      :wrestler-name="0"
      :wrestler-id="0"
      @close-addMatch="addMatchDialog = false"
    />

    <div class="fab-wrap">
      <v-menu
        v-model="fabMenu"
        :close-on-content-click="false"
        offset-y
        transition="scale-transition"
        bottom
        right
        origin="bottom right"
        content-class="fab-menu"
      >
        <template v-slot:activator="{ on }">
          <v-btn class="fab-main" color="red" dark fab v-on="on">
            <v-icon>{{ fabMenu ? "close" : "menu" }}</v-icon>
          </v-btn>
        </template>
        <v-list class="fab-actions">
          <v-list-tile to="/" @click="fabMenu = false">
            <v-list-tile-avatar>
              <v-icon>home</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Home</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            v-if="authenticated"
            @click="
              logOff();
              fabMenu = false;
            "
          >
            <v-list-tile-avatar>
              <v-icon>person_outline</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Log out</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile v-else to="/login" @click="fabMenu = false">
            <v-list-tile-avatar>
              <v-icon>person</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Log in</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile to="/user-guide" @click="fabMenu = false">
            <v-list-tile-avatar>
              <v-icon>menu_book</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>User Guide</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            @click="
              goBack();
              fabMenu = false;
            "
          >
            <v-list-tile-avatar>
              <v-icon>arrow_back</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Back</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            v-if="authenticated"
            @click="
              showAddWrestlerDialog();
              fabMenu = false;
            "
          >
            <v-list-tile-avatar>
              <v-icon>person_add</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Add wrestler</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            v-if="authenticated"
            @click="
              showAddMatchDialog();
              fabMenu = false;
            "
          >
            <v-list-tile-avatar>
              <v-icon>add_circle</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Add match</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            v-if="canManageLeagues"
            :to="{ name: 'league-manager' }"
            @click="fabMenu = false"
          >
            <v-list-tile-avatar>
              <v-icon>settings</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Manage leagues</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            v-if="canManageLeagues"
            :to="{ name: 'admin-dashboard' }"
            @click="fabMenu = false"
          >
            <v-list-tile-avatar>
              <v-icon>dashboard</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Admin dashboard</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile
            @click="
              $root.toggleDarkMode && $root.toggleDarkMode();
              fabMenu = false;
            "
          >
            <v-list-tile-avatar>
              <v-icon>{{
                $root.darkMode ? "brightness_5" : "brightness_3"
              }}</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>{{
                $root.darkMode ? "Light mode" : "Dark mode"
              }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script>
import { getAuth, signOut } from "firebase/auth";
import { authState } from "../utils/authState";
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDocsFromServer,
  orderBy,
  query,
  where,
  limit,
} from "firebase/firestore";
import { isLeagueHiddenFromHome } from "../utils/leagueVisibility";
import { store } from "../store";
import { formatSkillLevel } from "../utils/formatSkillDisplay";

export default {
  components: {
    "kennected-match-feed": () => import("./MatchFeed"),
    "kennected-add-wrestler": () => import("./AddWrestler"),
    "kennected-add-match": () => import("../components/AddMatch"),
    "database-totals-cards": () => import("./DatabaseTotalsCards.vue"),
  },
  data() {
    return {
      defaultLeagueLogo: require("../assets/rankster-logo-square.png"),
      leagues: [],
      fabMenu: false,
      addWrestlerDialog: false,
      addMatchDialog: false,
      discoverExpanded: false,
      discoverSearch: "",
      discoverLeague: null,
      discoverWeight: null,
      discoverMinSkill: null,
      discoverMaxSkill: null,
      discoverAthletes: [],
      discoverLoading: false,
      discoverHasSearched: false,
      leaguesLoading: false,
    };
  },
  computed: {
    authenticated: function () {
      return authState.user != null;
    },
    canManageLeagues: function () {
      if (!this.authenticated) return false;
      const auth = getAuth();
      const email = ((auth.currentUser && auth.currentUser.email) || "")
        .trim()
        .toLowerCase();
      const allowed = ["ken@kennected.com", "kennected.com@gmail.com"];
      return allowed.some((e) => e.toLowerCase() === email);
    },
    leagueNames: function () {
      return (this.leagues || [])
        .map((l) => l.data && l.data.Name)
        .filter(Boolean);
    },
    allWeights: function () {
      const set = new Set();
      (this.leagues || []).forEach((l) => {
        (l.weightclasses || []).forEach((wc) => {
          if (wc.Weight != null && wc.Weight !== "") set.add(String(wc.Weight));
        });
      });
      return Array.from(set).sort((a, b) => Number(a) - Number(b));
    },
    groupedLeaguesByState: function () {
      const grouped = {};
      (this.leagues || []).forEach((league) => {
        if (isLeagueHiddenFromHome(league.data)) return;
        const state =
          (league.data && league.data.State) != null &&
          String(league.data.State).trim() !== ""
            ? String(league.data.State).trim()
            : "Ungrouped";
        const leagueName =
          (league.data && league.data.League) != null &&
          String(league.data.League).trim() !== ""
            ? String(league.data.League).trim()
            : "Other";
        if (!grouped[state]) grouped[state] = {};
        if (!grouped[state][leagueName]) grouped[state][leagueName] = [];
        grouped[state][leagueName].push(league);
      });
      return Object.keys(grouped)
        .sort((a, b) =>
          a === "Ungrouped" ? 1 : b === "Ungrouped" ? -1 : a.localeCompare(b)
        )
        .map((name) => ({
          name,
          leagues: Object.keys(grouped[name])
            .sort((a, b) =>
              a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)
            )
            .map((leagueName) => ({
              name: leagueName,
              divisions: grouped[name][leagueName],
            })),
        }))
        .filter((s) => s.leagues && s.leagues.length > 0);
    },
    filteredDiscoverWrestlers: function () {
      let list = this.discoverAthletes || [];
      const q = (this.discoverSearch || "").trim().toLowerCase();
      if (q) {
        list = list.filter((w) => {
          const d = w.data || {};
          const first = String(d.FirstName || "").toLowerCase();
          const last = String(d.LastName || "").toLowerCase();
          const combined = `${first} ${last}`.trim();
          const fullName = String(d.FullName || combined).toLowerCase();
          const school = String(d.School || "").toLowerCase();
          return (
            combined.includes(q) ||
            fullName.includes(q) ||
            first.includes(q) ||
            last.includes(q) ||
            school.includes(q)
          );
        });
      }
      if (this.discoverLeague) {
        list = list.filter(
          (w) => (w.data && w.data.RankLeague) === this.discoverLeague
        );
      }
      if (this.discoverWeight != null && this.discoverWeight !== "") {
        list = list.filter(
          (w) =>
            (w.data && String(w.data.RankWeightClass)) ===
            String(this.discoverWeight)
        );
      }
      const minS =
        this.discoverMinSkill != null && this.discoverMinSkill !== ""
          ? Number(this.discoverMinSkill)
          : null;
      if (minS != null && !isNaN(minS)) {
        list = list.filter(
          (w) => (w.data.Skill != null ? Number(w.data.Skill) : 0) >= minS
        );
      }
      const maxS =
        this.discoverMaxSkill != null && this.discoverMaxSkill !== ""
          ? Number(this.discoverMaxSkill)
          : null;
      if (maxS != null && !isNaN(maxS)) {
        list = list.filter(
          (w) => (w.data.Skill != null ? Number(w.data.Skill) : 0) <= maxS
        );
      }
      return list;
    },
  },
  mounted: function () {
    this.loadLeagues();
  },
  methods: {
    formatSkillLevel,
    onLeagueLogoError(e) {
      if (e && e.target && this.defaultLeagueLogo)
        e.target.src = this.defaultLeagueLogo;
    },
    logOff: function () {
      const auth = getAuth();
      signOut(auth).then(
        () => {},
        () => {}
      );
    },
    goBack: function () {
      this.$router.go(-1);
    },
    getWeightclasses: function (docId) {
      return new Promise((resolve) => {
        const db = getFirestore();
        const q = query(
          collection(db, "leagues", docId, "weightclasses"),
          orderBy("Sort"),
          orderBy("Weight")
        );
        getDocs(q)
          .then(function (querySnapshot) {
            var rows = [];
            querySnapshot.forEach(function (doc) {
              rows.push(doc.data());
            });
            resolve(rows);
          })
          .catch(() => resolve([]));
      });
    },
    loadLeagues: async function () {
      this.leaguesLoading = true;
      this.leagues = [];
      try {
        const db = getFirestore();
        const q = query(collection(db, "leagues"), orderBy("Name"));
        let querySnapshot;
        try {
          querySnapshot = await getDocsFromServer(q);
        } catch (_e) {
          querySnapshot = await getDocs(q);
        }
        for (const docSnap of querySnapshot.docs) {
          const weightclasses = await this.getWeightclasses(docSnap.id);
          this.leagues.push({
            id: docSnap.id,
            data: docSnap.data(),
            weightclasses,
          });
        }
      } catch (_) {
        this.leagues = [];
      } finally {
        this.leaguesLoading = false;
      }
    },
    showAddWrestlerDialog: function () {
      this.addWrestlerDialog = true;
    },
    showAddMatchDialog: function () {
      this.addMatchDialog = true;
    },
    /**
     * Firestore prefix range is case-sensitive; try common casings so e.g.
     * "chatham" still matches School "Chatham Wrestling Club".
     */
    discoverPrefixVariants(prefix) {
      const t = String(prefix || "").trim();
      if (t.length < 2) return [];
      const lower = t.toLowerCase();
      const upper = t.toUpperCase();
      const title = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
      return Array.from(new Set([t, lower, upper, title])).filter(
        (p) => p.length >= 2
      );
    },
    /**
     * Prefix search on LastName, FirstName, FullName, and School (union, deduped).
     * Used when the search box has 2+ characters — with or without a league
     * filter (league is applied in filteredDiscoverWrestlers).
     */
    async fetchDiscoverRowsByPrefix(db, prefix, cap) {
      const perQueryLimit = 80;
      const prefixVariants = this.discoverPrefixVariants(prefix);
      const fields = ["LastName", "FirstName", "FullName", "School"];
      const rangeQueries = [];
      for (const pv of prefixVariants) {
        const end = pv + "\uf8ff";
        for (const field of fields) {
          rangeQueries.push(
            getDocs(
              query(
                collection(db, "athletes"),
                where(field, ">=", pv),
                where(field, "<=", end),
                limit(perQueryLimit)
              )
            )
          );
        }
      }
      const snaps = await Promise.all(rangeQueries);
      const byId = new Map();
      snaps.forEach((snap) => {
        snap.forEach((d) => {
          if (!byId.has(d.id)) {
            byId.set(d.id, { id: d.id, data: d.data() || {} });
          }
        });
      });
      return Array.from(byId.values()).slice(0, cap);
    },
    async runDiscoverQuery() {
      const league =
        this.discoverLeague != null && String(this.discoverLeague).trim() !== ""
          ? String(this.discoverLeague).trim()
          : "";
      const search = (this.discoverSearch || "").trim();
      if (!league && search.length < 2) {
        this.$root.$emit(
          "toast",
          "Choose a league or enter at least 2 characters (name or school).",
          "info"
        );
        return;
      }
      this.discoverLoading = true;
      this.discoverHasSearched = true;
      const db = getFirestore();
      const cap = 150;
      try {
        let rows = [];
        if (search.length >= 2) {
          rows = await this.fetchDiscoverRowsByPrefix(db, search, cap);
        } else if (league) {
          const loadLeagueOnly = async () => {
            const q = query(
              collection(db, "athletes"),
              where("RankLeague", "==", league),
              orderBy("Skill", "desc"),
              limit(cap)
            );
            const snap = await getDocs(q);
            const r = [];
            snap.forEach((d) => r.push({ id: d.id, data: d.data() || {} }));
            return r;
          };
          const weightRaw = this.discoverWeight;
          const hasWeight =
            weightRaw != null && String(weightRaw).trim() !== "";
          if (hasWeight) {
            const ws = String(weightRaw);
            const wn = Number(weightRaw);
            const tryWeight = async (wVal) => {
              const q = query(
                collection(db, "athletes"),
                where("RankLeague", "==", league),
                where("RankWeightClass", "==", wVal),
                orderBy("Skill", "desc"),
                limit(cap)
              );
              const snap = await getDocs(q);
              const r = [];
              snap.forEach((d) => r.push({ id: d.id, data: d.data() || {} }));
              return r;
            };
            try {
              rows = await tryWeight(ws);
              if (rows.length === 0 && !Number.isNaN(wn) && String(wn) !== ws) {
                rows = await tryWeight(wn);
              }
            } catch (_) {
              rows = await loadLeagueOnly();
              rows = rows.filter(
                (w) => String((w.data && w.data.RankWeightClass) ?? "") === ws
              );
            }
          } else {
            rows = await loadLeagueOnly();
          }
        }
        this.discoverAthletes = rows;
      } catch (_) {
        this.discoverAthletes = [];
        this.$root.$emit(
          "toast",
          "Could not load wrestlers. If this persists, add the Firestore index from the browser console link.",
          "error"
        );
      } finally {
        this.discoverLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.main-screen {
  min-height: 100%;
  background: #f5f5f7;
  padding-bottom: 4.5rem;
}

.content-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.75rem 0.75rem 0;
}

@media (min-width: 768px) {
  .content-wrap {
    padding: 1rem 1.5rem 0;
  }
}

.leagues-section {
  margin-bottom: 1.5rem;
}

.leagues-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.leagues-empty {
  width: 100%;
  padding: 1.5rem 1rem;
  text-align: center;
  background: #fff;
  border-radius: 14px;
  border: 1px dashed #e5e7eb;
}

.leagues-empty-msg {
  margin: 0 0 0.25rem;
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

.leagues-empty-hint {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.leagues-empty-btn {
  text-transform: none;
  font-weight: 600;
}

.section-title {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0 0.25rem;
}

.leagues-state-group {
  margin-bottom: 1.25rem;
}

.leagues-state-group:last-child {
  margin-bottom: 0;
}

.leagues-state-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.5rem;
  padding-bottom: 0.35rem;
  border-bottom: 2px solid #e5e7eb;
}

.leagues-state-inner {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  align-items: flex-start;
}

.leagues-league-group {
  flex: 1 1 220px;
  min-width: 0;
  margin-bottom: 0;
}

.leagues-league-group:last-child {
  margin-bottom: 0;
}

.leagues-league-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 0.5rem;
  padding-left: 0.25rem;
}

.leagues-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: stretch;
}

@media (min-width: 768px) {
  .leagues-row {
    gap: 0.5rem;
  }
}

/* Fixed width — flex-grow was stretching single cards / uneven rows (e.g. “IKWF Senior”). */
.league-card {
  flex: 0 0 106px;
  width: 106px;
  max-width: 106px;
  min-width: 0;
}

@media (min-width: 768px) {
  .league-card {
    flex: 0 0 120px;
    width: 120px;
    max-width: 120px;
  }
}

.league-card-inner {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 0.45rem 0.35rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  height: 100%;
  box-sizing: border-box;
}

.league-card-inner:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.league-card-logo {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  flex-shrink: 0;
}

.league-card-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.league-card-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
  hyphens: auto;
  width: 100%;
  min-height: 2.4em;
  max-height: 2.4em;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.league-card-btn {
  margin-top: 0;
  text-transform: none;
  font-weight: 600;
  display: inline-block;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.625rem;
  line-height: 1.2;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
  white-space: nowrap;
}

.match-feed-section {
  margin-bottom: 1rem;
}

.database-totals-section {
  margin-bottom: 1rem;
}

.fab-wrap {
  position: fixed;
  bottom: max(1rem, env(safe-area-inset-bottom, 0px));
  right: max(1rem, env(safe-area-inset-right, 0px));
  z-index: 4;
}

@media (min-width: 768px) {
  .fab-wrap {
    bottom: 1.5rem;
    right: 1.5rem;
  }
}

.fab-main {
  box-shadow: 0 4px 14px rgba(183, 36, 36, 0.4) !important;
}

.fab-menu {
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 200px;
}

.fab-actions .v-list__tile {
  height: 48px;
}

.discover-card {
  border-radius: 12px;
  overflow: hidden;
}

.discover-title {
  cursor: pointer;
  user-select: none;
}

.discover-body {
  padding: 0 16px 16px;
}

.discover-filters {
  margin-bottom: 12px;
}

.discover-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px;
  color: #6b7280;
}

.discover-hint {
  margin: 0.5rem 0 0.75rem;
  padding: 0 0.25rem;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.45;
}

.discover-search-btn-wrap {
  display: flex;
  align-items: flex-end;
}

.discover-search-btn {
  margin-bottom: 2px;
}

.discover-empty {
  padding: 24px;
  color: #6b7280;
  margin: 0;
}

.discover-results {
  max-height: 320px;
  overflow-y: auto;
}

.discover-row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.discover-row:hover {
  background: #f3f4f6;
}

.discover-avatar-wrap {
  flex-shrink: 0;
  margin-right: 12px;
}

.discover-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #eee;
}

.discover-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.discover-name {
  font-weight: 600;
  color: #1f2937;
}

.discover-meta,
.discover-record {
  font-size: 0.8rem;
  color: #6b7280;
}

.discover-arrow {
  color: #9ca3af;
  flex-shrink: 0;
}

/* Mobile: touch targets and spacing */
@media (max-width: 767px) {
  .content-wrap {
    padding: 0 0.5rem;
  }
  .league-card-inner {
    padding: 0.5rem 0.4rem;
  }
  .league-card-btn {
    padding: 3px 6px;
    font-size: 0.625rem;
  }
  .discover-row {
    min-height: 48px;
    padding: 12px 14px;
  }
  .section-title {
    font-size: 0.9rem;
  }
}

/* Dark mode */
.theme--dark .main-screen {
  background: #121212;
}
.theme--dark .league-card-inner {
  background: #1e1e1e;
  border-color: #374151;
}
.theme--dark .league-card-name {
  color: #e5e7eb;
}
.theme--dark .leagues-empty {
  background: #1e1e1e;
  border-color: #374151;
}
.theme--dark .leagues-empty-msg {
  color: #e5e7eb;
}
.theme--dark .leagues-empty-hint {
  color: #9ca3af;
}
.theme--dark .leagues-state-title {
  color: #e5e7eb;
  border-bottom-color: #374151;
}
.theme--dark .leagues-league-title {
  color: #9ca3af;
}
.theme--dark .discover-row:hover {
  background: #2d2d2d;
}
.theme--dark .discover-name {
  color: #e5e7eb;
}
.theme--dark .discover-meta,
.theme--dark .discover-record {
  color: #9ca3af;
}

.home-attribution {
  text-align: center;
  font-size: 0.75rem;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.52);
  margin: 1.25rem 0 0.75rem;
  padding: 0 0.75rem;
}

.home-attribution-link {
  color: #1565c0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.home-attribution-link:hover {
  color: #0d47a1;
}

.theme--dark .home-attribution {
  color: rgba(255, 255, 255, 0.5);
}

.theme--dark .home-attribution-link {
  color: #64b5f6;
}

.theme--dark .home-attribution-link:hover {
  color: #90caf9;
}
</style>
